import { DefaultValue, selector } from 'recoil';
import { getCoverableInformationConfig } from '../../pages/CoverableInformationPage/CoverableInformationStepsConfig';
import { getClaimsHistoryConfig } from '../../pages/ClaimsHistoryPage/ClaimsHistoryStepsConfig';
import { getOnsaCurrentProductSelector } from './getOnsaCurrentProductSelector';
import type { Step, StepInfo } from '../types/onsaTypes';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { isLoggedOnSelector } from './isLoggedOnSelector';
import { getAdditionalInformationConfig } from '../../pages/AdditionalInformationPage/additionalInformationStepsConfig';
import { carReplacementsAtom } from '../atoms/carReplacementsAtom';
import { wizardPages } from '../../config/wizardPagesConfig';
import { wizardStepAtom } from '../atoms/wizardAtom';
import { identificationAtom } from '../atoms/identificationAtom';
import { claimsHistorySelector } from './claimsHistorySelector';
import { allCoverablesAtom } from '../atoms/allCoverablesAtom';
import { getPostLoginQuestionsConfig } from '../../pages/PostLoginQuestionsPage/PostLoginQuestionStepsConfig';
import { isVehicleSeniorityInheritedSelector } from './isVehicleSeniorityInheritedSelector';
import { onsaProgressAtom } from '../atoms/onsaProgressAtom';

export const onsaProgressSelector = selector<Step>({
    key: 'getOnsaProgress',
    get: ({ get }) => {
        const currentStep = get(onsaProgressAtom);
        const currentPage = get(wizardStepAtom);
        const currentProduct = get(getOnsaCurrentProductSelector);
        if (!currentProduct) {
            return {
                title: '',
                currentStep: 1,
                currentStepName: 'ProductSelectorPage',
                totalSteps: 1,
                prevStep: 'ProductSelectorPage',
                nextStep: 'CoverableInformationPage'
            };
        }
        const shoppingBasketCoverables = get(allCoverablesAtom);
        const onsaCoverables = get(allCoverablesOnsaAtom);
        const coverableConfig = getCoverableInformationConfig(currentProduct, onsaCoverables);
        const shouldSendClaimsHistory = get(claimsHistorySelector);
        const isVehicleSeniorityInherited = get(isVehicleSeniorityInheritedSelector);
        const claimsConfig = shouldSendClaimsHistory
            ? getClaimsHistoryConfig(currentProduct, onsaCoverables)
            : [];
        const additionalInfoConfig = getAdditionalInformationConfig(currentProduct);
        const carReplacements = get(carReplacementsAtom);
        const isLoggedOn = get(isLoggedOnSelector);
        const postLoginConfig = getPostLoginQuestionsConfig(
            currentProduct,
            shoppingBasketCoverables,
            onsaCoverables,
            isLoggedOn,
            !!carReplacements,
            isVehicleSeniorityInherited,
            shouldSendClaimsHistory
        );
        const identificationInfo = get(identificationAtom);
        const loginStep = !isLoggedOn && !identificationInfo.cpr ? 1 : 0;
        const coverableInfoSteps = coverableConfig.steps.filter((step) => !step.disabled).length;
        const claimsHistorySteps = claimsConfig.filter((step) => !step.disabled).length;
        const additionalInfoSteps = additionalInfoConfig.length;
        const postLoginSteps = postLoginConfig.filter((step) => !step.disabled).length;

        const stepList: StepInfo[] = [
            { page: 'ProductSelectorPage', stepNames: ['ProductSelectorPage'], numberOfsteps: 1 },
            {
                page: 'CoverableInformationPage',
                stepNames: coverableConfig.steps
                    .filter((step) => !step.disabled)
                    .map((step) => step.id),
                numberOfsteps: coverableInfoSteps
            },
            {
                page: 'ClaimsHistoryPage',
                stepNames: claimsConfig.filter((step) => !step.disabled).map((step) => step.id),
                numberOfsteps: claimsHistorySteps
            },
            {
                page: 'AdditionalInformationPage',
                stepNames: additionalInfoConfig.map((step) => step.id),
                numberOfsteps: additionalInfoSteps
            },
            {
                page: 'CustomerIdentificationPage',
                stepNames: loginStep ? ['CustomerIdentificationPage'] : [],
                numberOfsteps: loginStep
            },
            {
                page: 'CprQuestionPage',
                stepNames: loginStep ? ['CprQuestionPage'] : [],
                numberOfsteps: loginStep
            },
            {
                page: 'PostLoginQuestionsPage',
                stepNames: postLoginConfig.filter((step) => !step.disabled).map((step) => step.id),
                numberOfsteps: postLoginSteps
            },
            { page: 'CoveragePackagePage', stepNames: ['CoveragePackagePage'], numberOfsteps: 1 }
        ];

        const currentStepIndex = stepList.findIndex(
            (e: StepInfo) => e.page === wizardPages[currentPage - 1].name
        );

        const currentStepName = stepList.flatMap((info) => info.stepNames)[currentStep];

        const nextPage =
            stepList.slice(currentStepIndex + 1).find((e) => e.numberOfsteps > 0)?.page ??
            'AdditionalCoveragePage';

        const previousPage = stepList
            .slice(0, currentStepIndex)
            .reverse()
            .find((e: StepInfo) => e.numberOfsteps > 0)?.page;

        if (nextPage === undefined || previousPage === undefined) {
            throw new Error('ONSA Navigation error');
        }

        return {
            title: coverableConfig.title,
            currentStep,
            currentStepName,
            totalSteps:
                coverableInfoSteps +
                claimsHistorySteps +
                additionalInfoSteps +
                loginStep +
                postLoginSteps,
            prevStep: previousPage,
            nextStep: nextPage
        };
    },
    set: ({ set }, newValue) => {
        set(onsaProgressAtom, newValue instanceof DefaultValue ? 1 : newValue.currentStep);
    }
});
